import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import { TREATMENT_REPORT_LABEL_KEYS } from '../types';
import {
  normalizeNonPrintColumn,
  toBackendNonPrintColumn,
} from '../utils';

interface UseTreatmentReportLabelChecksParams {
  setValue: UseFormSetValue<TreatmentReportFormData>;
  watch: UseFormWatch<TreatmentReportFormData>;
}

export const useTreatmentReportLabelChecks = ({
  setValue,
  watch,
}: UseTreatmentReportLabelChecksParams) => {
  const nonPrintColsFromForm = (watch('non_print_cols') ?? []) as string[];
  const normalizedNonPrintCols = useMemo(
    () => nonPrintColsFromForm.map(normalizeNonPrintColumn),
    [nonPrintColsFromForm]
  );

  const [labelChecks, setLabelChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(TREATMENT_REPORT_LABEL_KEYS.map(k => [k, !normalizedNonPrintCols.includes(k)]))
  );
  const isSyncingRef = useRef(false);
  const nonPrintColsKeyRef = useRef(normalizedNonPrintCols.slice().sort().join(','));

  useEffect(() => {
    if (isSyncingRef.current) {
      isSyncingRef.current = false;
      return;
    }

    const key = normalizedNonPrintCols.slice().sort().join(',');
    if (key !== nonPrintColsKeyRef.current) {
      nonPrintColsKeyRef.current = key;
      setLabelChecks(
        Object.fromEntries(TREATMENT_REPORT_LABEL_KEYS.map(k => [k, !normalizedNonPrintCols.includes(k)]))
      );
    }
  }, [normalizedNonPrintCols]);

  useEffect(() => {
    const unchecked = TREATMENT_REPORT_LABEL_KEYS.filter(k => !labelChecks[k]);
    const key = unchecked.slice().sort().join(',');
    if (key !== nonPrintColsKeyRef.current) {
      nonPrintColsKeyRef.current = key;
      isSyncingRef.current = true;
      const backendCols = unchecked.map(toBackendNonPrintColumn);
      setValue('non_print_cols', [...backendCols], { shouldDirty: false });
    }
  }, [labelChecks, setValue]);

  const toggleLabel = useCallback((key: string) => {
    setLabelChecks(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const allChecked = TREATMENT_REPORT_LABEL_KEYS.every(k => labelChecks[k]);
  const anyChecked = TREATMENT_REPORT_LABEL_KEYS.some(k => labelChecks[k]);
  const isIndeterminate = anyChecked && !allChecked;

  const toggleAll = useCallback(() => {
    const newVal = !TREATMENT_REPORT_LABEL_KEYS.every(k => labelChecks[k]);
    setLabelChecks(Object.fromEntries(TREATMENT_REPORT_LABEL_KEYS.map(k => [k, newVal])));
  }, [labelChecks]);

  return {
    allChecked,
    isIndeterminate,
    labelChecks,
    toggleAll,
    toggleLabel,
  };
};

